import React, {
	ComponentType,
	FunctionComponent,
	memo,
	MemoExoticComponent,
	useCallback,
	useMemo,
} from 'react';
import cn from 'classnames';

import { SpinnerOverlay } from '@components/common';

import { ITabProps } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/interfaces';

import styles from './Tabs.module.scss';

export interface ITabItem<T = any> {
	component: MemoExoticComponent<T | any> | FunctionComponent<| any> | ComponentType<any>;
	id: string;
	isActive: boolean;
	isDisabled?: boolean;
	name: string;
	schema?: any;
	fields?: string[];
	type?: string;
	isError?: boolean;
}

interface ITabsProps<T extends ComponentType<any> = any> {
	setTabState: (id: string) => Promise<void>;
	tabs: ITabItem<T>[];
	tabProps?: T;
	isLoading?: boolean;
	contentClassName?: string;
}

function Tabs({
	setTabState,
	tabs,
	tabProps,
	isLoading,
	contentClassName,
}: ITabsProps) {
	const handleSwitchTab = useCallback((id: string) => () => {
		if (tabs.find(item => item.id === id)?.isDisabled) {
			return;
		}

		void setTabState(id);
	}, [setTabState, tabs]);

	const activeTab: ITabItem<ITabProps> | undefined = useMemo(() => {
		return tabs.find((item: ITabItem<ITabProps>) => item.isActive);
	}, [tabs]);

	const ActiveTabComponent: ComponentType<ITabProps> | undefined = useMemo(() => {
		return activeTab?.component;
	}, [activeTab?.component]);

	const componentProps: any = useMemo(() => {
		return { ...tabProps, formMethods: tabProps.formMethods, formState: tabProps.formState };
	}, [tabProps]);

	return (
		<div className={styles.container}>
			<div className={styles.tabs}>
				{tabs.map(({
					id,
					isActive,
					name,
					isDisabled,
					isError,
				}: ITabItem<ITabProps>) => (
					<React.Fragment key={id}>
						<div
							className={cn(styles.tabItem, {
								[styles.activeTab]: isActive,
								[styles.disabled]: isDisabled,
								[styles.error]: isError,
							})}
							onClick={handleSwitchTab(id)}
						>
							{name}
						</div>
					</React.Fragment>
				))}
			</div>
			{ActiveTabComponent && (
				<div className={cn(styles.content, contentClassName)}>
					<ActiveTabComponent {...componentProps} />
				</div>
			)}
			{isLoading && <SpinnerOverlay />}
		</div>
	);
}

export default memo(Tabs);
